import { formatNumber } from '@agro1desenvolvimento/utils-js';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { first, sortBy } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FormEvent } from 'primereact/ts-helpers';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { NO_BREAK_SPACE } from '../../../../utils/constants';
import useDropdownFilter from '../../../../hooks/use-dropdown-filter';
import {
  Talhao,
  ItemSaldo,
  TalhaoRow,
  UnidadeConversao,
} from '../../../../@types';
import { RecomendacaoSemeaduraFormType } from '../types';
import {
  AtividadesService,
  ItensSaldoService,
  UnidadesService,
} from '../../../../services';
import useAsyncFetch from '../../../../hooks/use-async';
import { FormTalhao } from './form-controller';
import { getAreaDisponivel, getDataEstimadaParaAplicar } from './utils';
import maximumDecimalDigits from '../../../../utils/maximum-decimal-digits';
import { convertCultivares } from '../../utils';
import Agro1Loader from '../../../../components/Agro1Loader';
import Agro1Toast from '../../../../components/Agro1Toast';
import useRecomendacaoSemeaduraStore from '../store';
import ItemSaldoOptionTemplate from '../../../../components/ItemSaldoOptionTemplate';
import convertUnidade from '../../../../utils/convert-unidade';

const Form: FC<PropTypes> = ({
  formObject,
  editingTalhaoIdentification,
  onCancel,
  recomendacaoSemeadura,
  onSubmit,
}) => {
  const setCultivares = useRecomendacaoSemeaduraStore(
    (state) => state.setCultivares,
  );
  const itensSaldo = useRecomendacaoSemeaduraStore((store) => store.cultivares);
  const isEditing = !!editingTalhaoIdentification;
  const [unidades, setUnidades] = useState<UnidadeConversao[]>([]);
  const [loadingCultivares, setLoadingCultivares] = useState(false);
  const { filterQuery: cultivarFilterQuery, onFilter: onFilterCultivar } =
    useDropdownFilter();

  const [
    propriedade,
    talhoes,
    rendimentoOperacionalMaximo,
    atividade,
    sementeCobertura,
  ] = formObject.form.watch([
    'propriedade',
    'talhoes',
    'rendimentoOperacionalMaximo',
    'atividade',
    'sementeCobertura',
  ]);

  const formTalhao = useFormContext<TalhaoRow>();
  const {
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = formTalhao;
  const [itemSaldo, dataInicio, area, dataTerminoMinima] = watch([
    'itemSaldo',
    'dataInicio',
    'area',
    'dataTerminoMinima',
  ]);

  const showPMS = useMemo(() => {
    if (!atividade) return true;

    return !AtividadesService.isMilho(atividade.descricao);
  }, [atividade]);

  const areaClassName = useMemo(() => {
    const columnSize = sementeCobertura ? '2' : '3';

    return `col-6 md:col-${columnSize}`;
  }, [sementeCobertura]);

  const glebas = useMemo(
    () => sortBy(propriedade?.glebas, 'descricao'),
    [propriedade?.glebas],
  );

  const setDefaultArea = useImmutableCallback(() => {
    const talhaoAtual = getValues('talhao');

    if (talhaoAtual) {
      const areaDisponivel = getAreaDisponivel(
        talhoes.filter(
          ({ identification }) =>
            identification !== editingTalhaoIdentification,
        ),
        talhaoAtual,
      );

      setValue('area', maximumDecimalDigits(areaDisponivel, 2));
    } else {
      setValue('area', null);
    }
  });

  const setDefaultDataInicio = useImmutableCallback(() => {
    const talhaoAtual = getValues('talhao');

    const lastTalhao = talhoes.findLast(
      ({ identification }) => identification !== editingTalhaoIdentification,
    );

    if (!lastTalhao || !talhaoAtual) return;

    setValue('dataInicio', lastTalhao.dataTermino);
  });

  const setDefaultDataTermino = useImmutableCallback(() => {
    const [dataBase, areaAplicada] = getValues(['dataInicio', 'area']);
    const rendimentoOperacional = formObject.form.getValues(
      'rendimentoOperacionalMinimo',
    );

    if (!areaAplicada || !dataBase || !rendimentoOperacional) {
      return;
    }

    const novaDataTermino = getDataEstimadaParaAplicar({
      dataBase,
      areaAplicada,
      rendimentoOperacional,
    });

    setValue('dataTermino', novaDataTermino, { shouldValidate: true });
  });

  const onTalhaoChange = () => {
    setDefaultArea();
    setDefaultDataInicio();
    setDefaultDataTermino();
  };

  const cultivarFilterFirstRender = useRef(true);
  useEffect(() => {
    if (
      cultivarFilterFirstRender.current &&
      cultivarFilterQuery === undefined
    ) {
      cultivarFilterFirstRender.current = false;
      return;
    }
    cultivarFilterFirstRender.current = false;
    if (!propriedade) return;

    let cancelled = false;
    setLoadingCultivares(true);
    setCultivares([]);
    ItensSaldoService.saldos({
      propriedadeId: propriedade.id,
      operacaoItem: 'cultivar',
      descricao: cultivarFilterQuery,
    })
      .then((data) => {
        if (!cancelled) setCultivares(data);
      })
      .catch(() => {
        Agro1Toast.show({
          detail: 'Falha ao carregar itens saldo.',
          severity: 'error',
          sticky: true,
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingCultivares(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- propriedade nao esta nas deps pois nao queremos que de trigger nesse cara na troca de propriedade, pois na troca da propriedade ja vai ser chamado o fetch dos itens.
  }, [cultivarFilterQuery, setCultivares]);

  const { data: unidadeKg } = useAsyncFetch(
    {
      errorMessage: 'Falha ao buscar unidades.',
      callback: async () => UnidadesService.getUnidadeKg(),
      withLoader: false,
    },
    [],
  );

  const autoFillCultivarRepetida = useImmutableCallback(
    (newItemSaldo: ItemSaldo, newUnidades: UnidadeConversao[]) => {
      const lastLine = talhoes.findLast(
        (row) => row.itemSaldo?.id === newItemSaldo.id,
      );

      setValue('ciclo', lastLine?.ciclo ?? null, { shouldValidate: true });

      if (sementeCobertura) {
        setValue('dose', lastLine?.dose ?? null, { shouldValidate: true });
        if (unidadeKg) {
          setValue('unidade', convertUnidade(unidadeKg), {
            shouldValidate: true,
          });
        }
        return;
      }

      let unidade = lastLine?.unidade ?? null;

      if (!unidade) {
        const selectedUnidade = first(newUnidades);
        unidade = selectedUnidade ?? null;
      }

      setValue('unidade', unidade, { shouldValidate: true });
      setValue('densidade', lastLine?.densidade ?? null, {
        shouldValidate: true,
      });
      setValue('pms', lastLine?.pms ?? null, { shouldValidate: true });
      setValue('pg', lastLine?.pg ?? null, { shouldValidate: true });
    },
  );

  const talhaoOptionTemplate = useImmutableCallback((gleba: Talhao | null) => {
    if (!gleba) return NO_BREAK_SPACE;

    const areaDisponivel = getAreaDisponivel(talhoes, gleba);
    const areaDisponivelFormatted = formatNumber(areaDisponivel, {
      maximumFractionDigits: 2,
      useGrouping: true,
    });

    return `${gleba.descricao} (${areaDisponivelFormatted}) ha`;
  });

  useEffect(() => {
    if (!itemSaldo || !itensSaldo?.length) return;

    if (!itensSaldo.find((item) => item.id === itemSaldo.id)) {
      setValue('itemSaldo', null);
    }
  }, [itensSaldo, itemSaldo, setValue]);

  useEffect(() => {
    if (!recomendacaoSemeadura) return;

    const setTalhoes = async () => {
      try {
        const talhoesToLoad = await convertCultivares(recomendacaoSemeadura);

        formObject.form.setValue('talhoes', talhoesToLoad);
      } catch (error) {
        console.error(error);
        Agro1Toast.show({
          detail: 'Falha ao carregar itens saldo.',
          severity: 'error',
          sticky: true,
        });
      }
    };

    setTalhoes();
  }, [formObject.form, recomendacaoSemeadura]);

  useEffect(() => {
    if (dataInicio && area && rendimentoOperacionalMaximo) {
      const novaData = getDataEstimadaParaAplicar({
        dataBase: dataInicio,
        areaAplicada: area,
        rendimentoOperacional: rendimentoOperacionalMaximo,
      });
      setValue('dataTerminoMinima', novaData);
    } else {
      setValue('dataTerminoMinima', null);
    }
  }, [area, dataInicio, rendimentoOperacionalMaximo, setValue]);

  const fetchUnidades = useCallback(async (unidadeId: number) => {
    const loaderId = Agro1Loader.show();
    let newUnidades;
    try {
      newUnidades = await UnidadesService.listConversao(unidadeId);

      setUnidades(newUnidades || []);
    } catch (error) {
      console.error(error);
      Agro1Toast.show({
        detail: 'Falha ao carregar unidades.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }

    return newUnidades;
  }, []);

  useEffect(() => {
    if (!editingTalhaoIdentification) return;

    if (!itemSaldo) {
      setUnidades([]);
      return;
    }

    fetchUnidades(+itemSaldo.unidade.id);
  }, [itemSaldo, editingTalhaoIdentification, fetchUnidades]);

  const onChangeCultivar = useImmutableCallback(
    async (event: FormEvent<ItemSaldo>) => {
      if (!event.value) return;

      const newUnidades = await fetchUnidades(+event.value.unidade.id);
      autoFillCultivarRepetida(event.value, newUnidades || []);
    },
  );

  return (
    <div className="recomendacoes-semeadura-create-edit-talhoes col-12">
      <div className="formgrid grid mt-5">
        <FormTalhao.Dropdown
          options={glebas}
          dataKey="id"
          optionLabel="descricao"
          label="Talhão:"
          name="talhao"
          containerClassName="field col-6 md:col-5 mb-0"
          onChange={onTalhaoChange}
          itemTemplate={talhaoOptionTemplate}
          valueTemplate={talhaoOptionTemplate}
          dependencies={['itemSaldo']}
        />
        <FormTalhao.Dropdown
          options={itensSaldo}
          dataKey="id"
          optionLabel="descricao"
          label="Cultivar:"
          name="itemSaldo"
          containerClassName="field col-6 md:col-5 mb-0"
          itemTemplate={ItemSaldoOptionTemplate}
          emptyMessage="Nenhum resultado encontrado"
          onChange={onChangeCultivar}
          dependencies={['talhao']}
          filter
          loading={loadingCultivares}
          dropdownIcon={loadingCultivares ? 'pi pi-spin pi-spinner' : undefined}
          onFilter={onFilterCultivar}
        />
        <FormTalhao.Dropdown
          options={unidades}
          dataKey="id"
          optionLabel="abreviatura"
          label="Unidade:"
          name="unidade"
          containerClassName="field col-6 md:col-2 mb-0"
          visible={!sementeCobertura}
        />
        <FormTalhao.InputNumber
          label="Área:"
          name="area"
          addonRight="ha"
          containerClassName={areaClassName}
        />
        <FormTalhao.InputNumber
          label="Dose:"
          name="dose"
          addonRight="kg/ha"
          containerClassName="field col-6 md:col-3 mb-0"
          visible={sementeCobertura}
        />
        <FormTalhao.InputNumber
          label="Densidade:"
          name="densidade"
          addonRight="pl/ha"
          containerClassName="field col-6 md:col-3 mb-0"
          visible={!sementeCobertura}
        />
        {showPMS && (
          <FormTalhao.InputNumber
            label="PMS:"
            name="pms"
            addonRight="gr"
            containerClassName="field col-6 md:col-3 mb-0"
            visible={!sementeCobertura}
          />
        )}
        <FormTalhao.InputNumber
          label="PG:"
          name="pg"
          addonRight="%"
          containerClassName="field col-6 md:col-3 mb-0"
          visible={!sementeCobertura}
        />
        <FormTalhao.InputNumber
          label="Ciclo:"
          name="ciclo"
          addonRight="dias"
          containerClassName="field col-6 md:col-3 mb-0"
        />
        <FormTalhao.Calendar
          name="dataInicio"
          label="Data Início:"
          containerClassName="field col-6 md:col-3 mb-0"
          className="w-full"
          addonRight={<i className="pi pi-calendar" />}
          onChange={setDefaultDataTermino}
        />
        <FormTalhao.Calendar
          name="dataTermino"
          label="Data Término:"
          containerClassName="field col-6 md:col-3 mb-0"
          className="w-full"
          addonRight={<i className="pi pi-calendar" />}
          minDate={dataTerminoMinima ?? undefined}
        />
      </div>
      <div className="flex justify-content-end">
        <div className="flex flex-row">
          {isEditing && (
            <Button
              type="button"
              className="button-danger mr-1"
              label="Cancelar"
              onClick={onCancel}
            />
          )}
          <Button
            type="button"
            className="button-secondary"
            label="Salvar"
            disabled={!isValid}
            onClick={onSubmit}
          />
        </div>
      </div>
    </div>
  );
};

type PropTypes = RecomendacaoSemeaduraFormType & {
  editingTalhaoIdentification: string | null;
  onCancel: () => void;
  onSubmit: () => void;
};

export default Form;
